<template>
    <div class="scrollable-pane">
        <div class="scrollable-pane-content">

            <select-import-template
                v-if="!importData || importData.length === 0"
                v-on:processDataClicked="uploadCsv"
                :project-id="projectId"
                :progress-status-text="progressStatusText"
                :fields-list="fieldsList"
                :date-fields="dateFields"
                type="contacts"
                show-ignore-duplicates-toggle="yes"
                title-string="import_contacts_page.title"
                import-button-string="import_contacts_page.import_button"
            />

            <contact-import-process
                v-if="!!importData && importData.length > 0"
                :import-data="importData"
                :contact-custom-fields="contactCustomFields"
                :progress-status-text="progressStatusText"
                :imported-duplicates="importedDuplicates"
                v-on:startImport="startImport"
                v-on:cancel="cancelImport"
                v-on:importCompleted="importCompleted"
                :duplicateFieldSettings.sync="duplicateFieldSettings"
                ref="importProcessComponent"
            />

        </div>
    </div>
</template>
<script>

import SelectImportTemplate from './components/SelectImportTemplate.vue';
import ProgressBar from './components/ProgressBar.vue';
import ContactImportProcess from './pages/ContactImportProcess.vue';
import {t} from '../../../i18n/i18n.js';
import app from '../../../App.js';
import axios from 'axios';
import MessagePopupView from '../../../../controls/MessagePopupView.js';
import {CONTACT_BASIC_FIELDS} from '../../../../Globals';
import CustomFields from '../../../../shared_utils/CustomFields';

export default {
    components: {ContactImportProcess, ProgressBar, SelectImportTemplate},
    props: ['projectId'],
    data() {
        return {
            contactCustomFields: [],
            importTaskId: undefined,
            timeoutIdForTask: undefined,
            importData: [],
            progressCount: undefined,
            ignoreDuplicates: false,
            importProgress: undefined,
            duplicateFieldSettings: app.getUserSetting('contacts_import_field_settings') || {},
            importedDuplicates: [],
        }
    },

    mounted() {
        this.getCustomFields();
    },

    beforeDestroy() {
        this.stopWaitingForTask();
    },

    methods: {
        async getCustomFields() {

            app.AlertUI.showLoader();

            try {
                const customFieldsUrl = `projects/${this.projectId}/contact_custom_fields`;

                const {data} = await axios.get(app.apiUrl(customFieldsUrl));

                this.contactCustomFields = data

            } catch (err) {
                this.contactCustomFields = [];
                app.showReqError(err);
            }

            app.AlertUI.hideLoader();
        },

        cancelImport() {
            this.stopWaitingForTask();
            this.importData = [];
            this.importedDuplicates = [];
        },

        async uploadCsv(file, templateId, ignoreDuplicates) {
            // Upload selected CSV file,
            // Get back parsed results with duplicates/errors info per row

            const formData = new FormData();

            formData.append('csv', file);
            formData.append('import_template_id', templateId);
            formData.append('project_id', this.projectId);
            if (ignoreDuplicates) {
                formData.append('ignore_duplicates', 'true');
            }

            try {
                const {data: {id: taskId}} = await axios
                    .post(app.apiUrl('contacts/import/process_csv'), formData);

                let results = await this.checkForTaskProgress(taskId);

                if (results && results.length === 0) {
                    new MessagePopupView({
                        addClass: 'error', message: t('import_contacts_page.error_no_contacts'), queue: true
                    }).show();
                }

                for (let result of results) {
                    if (result.duplicates && result.duplicates.length > 0) {
                        result.result = 'duplicate';
                    } else if (result.errors && result.errors.length > 0) {
                        result.result = 'error';
                    }
                }

                this.importData = Object.freeze(results);
            } catch (err) {
                app.showReqError(err);
            } finally {
                app.AlertUI.hideLoader();
            }

            this.$refs.importProcessComponent && this.$refs.importProcessComponent.activateRelevantTab();
        },

        async startImport() {

            const fieldsSettings = this.duplicateFieldSettings;

            const mergeFieldWithSettings = (source, merge, settings) => {
                if (source === merge) // It's the same, no need to test settings
                    return source;

                if (!merge) // It's a new field, make sure we can add
                    return settings.add ? source : merge;

                if (merge && !source) // We need to delete, make sure we can delete
                    return settings.del ? source : merge;

                if (merge && source) // We need to update, make sure we can update
                    return settings.update ? source : merge;

                return source;
            };

            const handleDuplicateData = (sourceData, duplicateData) => {
                let mergedData = {};

                for (const field of Object.keys(sourceData)) {
                    if (field === 'errors' || field === 'duplicates' || field === 'selected')
                        continue;

                    mergedData[field] = mergeFieldWithSettings(
                        sourceData[field],
                        duplicateData[field],
                        fieldsSettings[field]);
                }

                return mergedData;
            };

            let newContacts = this.importData.filter(c => !c.result);
            let duplicateContacts = this.importData.filter(c => c.selected && c.result === 'duplicate');

            let newContactRows = newContacts.map(c => ({'values': c.columns}));
            let duplicateContactRows = duplicateContacts.map(c => {
                let mergeContact = c.duplicates.shift();

                return {
                    values: handleDuplicateData(c.columns, mergeContact),
                    merge_id: mergeContact['id'],
                    ignore_ids: c.duplicates.map(x => x['contact_id']),
                };
            });

            const data = {
                'project_id': this.projectId,
                'rows': newContactRows.concat(duplicateContactRows),
            };

            app.AlertUI.showLoader(t('activity_indicator.saving'));

            let importResults;

            try {
                const {data: {id: taskId}} = await axios
                    .post(app.apiUrl('contacts/import/import_data'), data);

                importResults = await this.checkForTaskProgress(taskId);

            } catch (err) {
                app.showReqError(err);
            } finally {
                app.AlertUI.hideLoader();
            }

            if (!importResults) return;

            const contactsBatch = newContacts.concat(duplicateContacts);
            let tempImportData = Array.from(this.importData);

            for (let i = 0; i < contactsBatch.length; i++) {
                const contact = contactsBatch[i];
                const result = importResults[i];

                if (result.inserted_id) {
                    // This is a freshly imported contact

                    contact.result = 'ok';
                    delete contact.duplicates;
                    delete contact.errors;
                    continue;
                }

                if (result.updated_id) {
                    // It was merged into an existing contact

                    let original = tempImportData.find(x => x['id'] === result.updated_id);
                    if (original !== undefined) {
                        // The duplicate is inside current data
                        this.importedDuplicates.push(original);
                        Object.assign(original, contact);
                        original.originalResult = original.originalResult || original.result;
                        original.result = 'ok';
                        delete original.errors;
                        delete original.duplicates;
                        let idx = tempImportData.indexOf(contact);
                        if (idx !== -1) {
                            tempImportData.splice(idx, 1);
                        }
                    } else {
                        contact['id'] = result.updated_id;
                        contact.originalResult = contact.originalResult || contact.result;
                        contact.result = 'ok';
                        delete contact.duplicates;
                        delete contact.errors;
                    }
                    continue;
                }

                contact.errors = result.errors;

                if (result.duplicates && result.duplicates.length > 0) {
                    contact.result = 'duplicate';
                    contact.duplicates = result.duplicates;
                } else {
                    contact.result = 'error';
                }
            }

            // Trigger change without deep watchers
            this.importData = Object.freeze(tempImportData);

            if (this.importedDuplicates.length) {
                new MessagePopupView({
                    addClass: 'warning',
                    message: t('import_contacts_page.warn_imported_duplicates', {number: this.importedDuplicates.length}),
                    queue: true
                }).show();
            }

            setTimeout(() => {
                this.$refs.importProcessComponent && this.$refs.importProcessComponent.activateRelevantTab();
            }, 0);
        },

        async checkForTaskProgress(taskId) {

            this.importProgress = 0;

            while (true) {
                await new Promise(resolve => this.timeoutIdForTask = setTimeout(resolve, 3000));

                try {
                    const {data: response} = await axios.get(app.apiUrl(`contacts/import/tasks/${taskId}`));

                    if (response['end_time']) {
                        this.importProgress = undefined;

                        if (response['error']) {
                            new MessagePopupView({
                                addClass: 'error', message: t('import_contacts_page.error_generic_failure'), queue: true
                            }).show();

                            return;
                        }

                        return response.results;
                    } else {
                        this.importProgress = response['result_count'];
                    }

                } catch (err) {
                    this.importProgress = undefined;
                    app.showReqError(err);

                    if (!err.response || err.response.status !== 404) {
                        continue;
                    }

                    return;
                }
            }
        },

        stopWaitingForTask() {
            clearTimeout(this.timeoutIdForTask);
            this.progressCount = undefined;
        },

        importCompleted(newContactsCount) {
            if (newContactsCount === 0) return;
            new MessagePopupView({
                addClass: 'done', message: t('import_contacts_page.completed_prompt', {count: newContactsCount}), queue: true
            }).show();
        }

    },

    watch: {
        importData() {
            let duplicateFieldSettings = Object.create(null);

            if (this.importData && this.importData.length > 0) {
                const keys = Object.keys(this.importData[0].columns);

                for (let fieldKey of keys) {
                    let old = this.duplicateFieldSettings[fieldKey];

                    if (old) {
                        duplicateFieldSettings[fieldKey] = old;
                    } else {
                        let fieldName = t(`contact_fields.${fieldKey}`);

                        duplicateFieldSettings[fieldKey] = {
                            key: fieldKey,
                            label: fieldName,
                            add: true, update: true, del: true,
                        };
                    }
                }
            }

            this.duplicateFieldSettings = duplicateFieldSettings;
        },

        duplicateFieldSettings(settings) {
            app.setUserSetting('contacts_import_field_settings', settings);
        },
    },

    computed: {
        progressStatusText() {
            if (this.importProgress === undefined)
                return '';
            return t('import_contacts_page.import_status', {'count': this.importProgress});
        },
        fieldsList() {
            const basicContactFields = CONTACT_BASIC_FIELDS
                .map(field => ({
                        value: field,
                        text: t('contact_fields', field)
                    })
                );

            const customContactFields = this.contactCustomFields
                .map(field =>
                    Object.assign({
                        value: `f${field.id}`,
                        text: field.name
                    }, field)
                );

            return basicContactFields.concat(customContactFields)
        },
        dateFields() {

            const result = this.contactCustomFields
                .filter(field => field.subtype === CustomFields.SubType.date)
                .map(field => `f${field.id}`);

            result.push('birthdate');

            return result;

        },
    }
}

</script>
